import React, { useState, useMemo } from 'react';

import { connect } from 'react-redux';
import { useEffect } from 'react';
import { Button, Caption1Stronger, Caption2, Divider, Label, MessageBar, MessageBarBody, MessageBarTitle, Popover, PopoverSurface, PopoverTrigger, Text, Tooltip } from '@fluentui/react-components';

// component functions
import * as ComponentFunctions from './Protocol.Activity.Functions';

//icond
import { ArrowSync16Filled, ArrowSync16Regular, ArrowSync20Filled, CalendarSettingsRegular, Call12Filled, Call16Filled, Call16Regular, Phone16Regular, Phone28Regular, PhoneArrowRightFilled, SyncOff16Regular, Warning16Filled, Warning16Regular, Warning20Filled } from '@fluentui/react-icons';

// styles
import './Protocol.Activity.Styles.css';

// models
import { ProtocolPatient } from '../../../models/Entities/Protocols/ProtocolPatient';
import { PatientVisit } from '../../../models/Entities/Protocols/PatientVisist';
import Moment from 'react-moment';
import StageFixFormComponent from '../../Contracts/Stages/StageFixForm/StageFix.Form.Component';
import { Separator } from '@fluentui/react';
import ProtocolPatientReferrerComponent from './ProtocolPatientReferrer/Protocol.PatientReferrer.Component';


const ProtocolActivityComponent = (props: ComponentFunctions.Props) => {

    const [isOpen, setIsOpen] = useState(false);
    const openPanel = () => setIsOpen(true);
    const dismissPanel = () => setIsOpen(false);

    const [currentPatiens, setCurrentPatients] = useState<string[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshRequested, setRefreshRequested] = useState(false);

    useEffect(() => {
        props.GetMedicalProtocolById(props.ProtocolId);
        props.GetAllPractitioners(1, 1000, '');
    }, [props.ProtocolId]);

    useEffect(() => {
        if (props.Protocol?.patientVisits) {
            setCurrentPatients(props.Protocol.patientVisits.map((p: PatientVisit) => p.numberInProtocol).reduce(
                (acc: string[], current: string) => {
                    if (!acc.includes(current)) acc.push(current);
                    return acc;
                }, []));
        }
    }, [props.Protocol?.patientVisits]);

    useEffect(() => {
        if (props.Protocol?.id) {
            props.GetAllProtocolContractAssociations(props.Protocol.id);
            props.GetProtocolPatients(props.ProtocolId, null);
            props.GetPatientVisitByProtocolId(props.ProtocolId);
            props.GetVisitsByProtocolId(props.ProtocolId);
            props.GetHcProtocolPatientsByProtocolId(props.ProtocolId);
        }
    }, [props.Protocol?.id]);

    useEffect(() => {
        if (props.Protocol?.contractAssociation?.contractId) {
            props.GetContractById(props.Protocol.contractAssociation.contractId);
        }
    }, [props.Protocol?.contractAssociation?.contractId]);

    useEffect(() => {
        props.Contract?.id && props.GetAllContractsStage(props.Contract!.id!);
    }, [props.Contract?.id]);

    const visits = useMemo(
        () => ComponentFunctions.GetRealStages(props.Stages, props.Visits),
        [props.Stages, props.Visits]
    );

    const ResetInfo = () => {
        setRefreshRequested(true);
        setIsRefreshing(true);

        props.Contract?.id && props.GetAllContractsStage(props.Contract!.id!);
        props.GetProtocolPatients(props.ProtocolId, null);
        props.GetPatientVisitByProtocolId(props.ProtocolId);
        props.GetVisitsByProtocolId(props.ProtocolId);
        props.GetHcProtocolPatientsByProtocolId(props.ProtocolId);
    };

    useEffect(() => {
        if (!refreshRequested) {
            return;
        }

        if (!props.IsGettingVisits && !props.IsLoadingHcProtocolPatients) {
            setIsRefreshing(false);
            setRefreshRequested(false);
        }
    }, [refreshRequested, props.IsGettingVisits, props.IsLoadingHcProtocolPatients]);


    // Move hook call to top-level (was previously called inside VisitGrid causing hook order changes)
    const styles = ComponentFunctions.useStyles();

    const VisitGrid = (stage: ComponentFunctions.internalStage, visit: PatientVisit | null, patient: ProtocolPatient | null) => {

        const hasAlerts = (stage.isRemote && visit !== null && visit.visitType === 0)
            || (stage.isRemote && stage.remoteWarning)
            || !stage.stageId;

        if (visit) {
            return (
                <div className='visitDetail'>
                    {/* Header: título + modalidad */}
                    <div className='visitDetail__header'>
                        <Text weight='semibold' size={300}>{visit.title}</Text>
                        <span className='visitDetail__typeTag'>
                            {visit.visitType === 0 ? <Phone16Regular /> : <Call16Regular />}
                            {visit.visitType === 0 ? 'Presencial' : 'Telefónica'}
                        </span>
                    </div>

                    {/* Estado */}
                    <div className='visitDetail__status'>
                        <span className={'VisitActivity cirle '.concat(ComponentFunctions.GetVisitStatusByVisit(visit.status)).concat(!stage.stageId ? ' warning' : '')} />
                        <Text weight='semibold'>{ComponentFunctions.GetVisitStatusDescription(visit.status)}</Text>
                    </div>

                    <Divider style={{ width: 'auto' }} />

                    {/* Datos del paciente */}
                    <div className='visitDetail__fields'>
                        <div className='visitDetail__field'>
                            <Caption2>Paciente</Caption2>
                            <Caption1Stronger>{visit.numberInProtocol}</Caption1Stronger>
                        </div>
                        <div className='visitDetail__field'>
                            <Caption2>Fecha de realización</Caption2>
                            <Caption1Stronger><Moment date={visit.date} format='DD/MM/YYYY hh:mm a' /></Caption1Stronger>
                        </div>
                        <div className='visitDetail__field'>
                            <Caption2>Profesional médico</Caption2>
                            <Caption1Stronger>{props.GetPractitioner(visit.createdById)?.fullName || 'No encontrado'}</Caption1Stronger>
                        </div>
                        <ProtocolPatientReferrerComponent
                            numberInProtocol={visit.numberInProtocol}
                            protocolPatients={props.ProtocolPatients}
                        />
                        {/* { patient && <div className='visitDetail__field'>
                            <Caption2>Profesional médico derivador</Caption2>
                            <Text>{patient.medicalPractitionerId}</Text>
                            <Caption1Stronger>{props.GetPractitioner(patient.medicalPractitionerId)?.fullName || 'No encontrado'}</Caption1Stronger>
                        </div>} */}
                    </div>

                    {/* Alertas agrupadas */}
                    {hasAlerts && (
                        <div className='visitDetail__alerts'>
                            {stage.isRemote && visit.visitType === 0 && (
                                <MessageBar intent='warning'>
                                    <MessageBarBody>Esta visita es remota y se realizó de forma presencial.</MessageBarBody>
                                </MessageBar>
                            )}
                            {stage.isRemote && stage.remoteWarning && (
                                <MessageBar intent='warning'>
                                    <MessageBarBody>Esta visita es remota; el contrato y el template definen modalidad presencial.</MessageBarBody>
                                </MessageBar>
                            )}
                            {!stage.stageId && (
                                <MessageBar intent='warning'>
                                    <MessageBarBody>
                                        <MessageBarTitle>Atención: </MessageBarTitle>
                                        Esta visita fue realizada, pero no está definida en el contrato.
                                    </MessageBarBody>
                                </MessageBar>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className='visitDetail'>
                {/* Header: nombre de etapa + modalidad */}
                <div className='visitDetail__header'>
                    <Text weight='semibold' size={300}>{stage.stageName}</Text>
                    <span className='visitDetail__typeTag'>
                        {stage.isRemote ? <Call16Regular /> : <Phone16Regular />}
                        {stage.isRemote ? 'Telefónica' : 'Presencial'}
                    </span>
                </div>

                {/* Estado vacío */}
                <div className='visitDetail__emptyState'>
                    <SyncOff16Regular />
                    <Text>Visita no realizada</Text>
                </div>

                {!stage.stageId && (
                    <div className='visitDetail__alerts'>
                        <MessageBar intent='error'>
                            <MessageBarBody>
                                <MessageBarTitle>Atención: </MessageBarTitle>
                                Esta visita no fue realizada y no está definida en el contrato.
                            </MessageBarBody>
                        </MessageBar>
                    </div>
                )}
            </div>
        );
    };

    const getReferrerNameByPatient = (patientNumberInProtocol: string): string | undefined => {
        const protocolPatient = props.ProtocolPatients.find(
            (item) => item.numberInProtocol === patientNumberInProtocol && !!item.medicalPractitionerId
        );

        if (!protocolPatient?.medicalPractitionerId) {
            return undefined;
        }

        return props.GetPractitioner(protocolPatient.medicalPractitionerId)?.fullName;
    };

    const getOutOfContractClass = (patient: string, stage: ComponentFunctions.internalStage): string => {
        if (stage.stageId) {
            return '';
        }

        return ComponentFunctions.IsVisitExist(props.PatientVisits, patient, stage)
            ? ' warning-realized'
            : ' warning';
    };


    const Form = (
        <StageFixFormComponent
            isOpen={isOpen}
            openPanel={openPanel}
            dismissPanel={dismissPanel}
            ProtocolId={props.ProtocolId}
        />
    );

    return (
        <>
            {props.Patients.length === 0 && <MessageBar>
                <MessageBarBody>
                    <MessageBarTitle>No hay pacientes registrados</MessageBarTitle>
                    No se han econtrado pacientes registrados para este protocolo.{" "}
                </MessageBarBody>
            </MessageBar>}
            {props.ContractId === null && <MessageBar>
                <MessageBarBody>
                    <MessageBarTitle>No hay contrato asociado</MessageBarTitle>
                    No se han asociado un contrato a este protocolo.{" "}
                </MessageBarBody>
            </MessageBar>}
            <div className='visitButton'>
                {props.Contract?.id && <Button icon={<CalendarSettingsRegular />} onClick={openPanel}>Verificar asociación de visitas</Button>}
                <Tooltip content="Actualizar datos" relationship="label" withArrow>
                    <Button
                        icon={<ArrowSync16Regular className={isRefreshing ? 'refreshIconSpin' : ''} />}
                        onClick={ResetInfo}
                        aria-label="Actualizar"
                        disabled={isRefreshing}
                    />
                </Tooltip>
            </div>
            {visits?.length > 0 && (
                <div className='pa-legend' aria-label='Leyenda de estados de visita'>
                    <span className='pa-legend__title'>Estados</span>
                    <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--completed' />Completada</span>
                    <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--autocompleted' />Autocompletada</span>
                    <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--started' />Iniciada</span>
                    <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--changed' />Modificada</span>
                    <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--skipped' />Salteada</span>
                    <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--failed' />Falla</span>
                    <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--warning' />Fuera de contrato</span>
                    <span className='pa-legend__item'><span className='pa-legend__swatch pa-legend__swatch--warningRealized' />Fuera de contrato (realizada)</span>
                </div>
            )}
            {visits?.length > 0 && <div className='tableContainer'>
                <table className='table-header-rotated'>
                    <thead>
                        <tr >
                            <th style={{ minWidth: '17em' }}>Pacientes [{currentPatiens.length}]</th>
                            {visits.length > 0 && visits.map((stage: ComponentFunctions.internalStage) => (
                                <Tooltip key={`head-${stage.stageId ?? 'null'}-${stage.order}`} withArrow positioning={'below'} content={<><strong>{stage.stageName}</strong><br/><Text>[{stage.flowchartName}]</Text></>} relationship="label">
                                    <th className='rotate-45'>
                                        <div><span>{stage.stageName}{stage.flowchartName && stage.stageId && ' [ ' + stage.flowchartName + ' ]'}</span></div>
                                    </th>
                                </Tooltip>
                            ))} 
                        </tr>
                    </thead>
                    <tbody>
                        {currentPatiens?.map((patient: string) => {
                            const protocolPatient = props.ProtocolPatients.find(p => p.numberInProtocol === patient);
                            const referrerName = getReferrerNameByPatient(patient);
                            return (
                            <tr key={`patient-row-${patient}`}>
                                <th style={{ borderBottom: 'solid 1px lightgray' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <Text title={protocolPatient?.statusDescription}>{patient}</Text>
                                        {protocolPatient?.statusDescription && (
                                            <Tooltip relationship='label' withArrow content={protocolPatient.statusDescription}>
                                                <span className="pa-patient-badge pa-patient-badge--status">
                                                    {protocolPatient.statusDescription.charAt(0).toUpperCase()}
                                                </span>
                                            </Tooltip>
                                        )}
                                        {referrerName && (
                                            <Tooltip relationship='label' withArrow content={`Derivado por: ${referrerName}`}>
                                                <span className="pa-patient-badge pa-patient-badge--referral">D</span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </th>
                                {visits?.map((stage: ComponentFunctions.internalStage) => <td key={`cell-${patient}-${stage.stageId ?? 'null'}-${stage.order}`}>
                                    <Popover withArrow>
                                        <PopoverTrigger disableButtonEnhancement>
                                            <div className={'VisitActivity '
                                                .concat(ComponentFunctions.GetVisitStatus(props.PatientVisits, patient, stage))
                                                .concat(getOutOfContractClass(patient, stage))}>
                                                {stage.isRemote &&
                                                    (ComponentFunctions.GetVisitByPatientAndStage(props.PatientVisits, patient, stage) ?
                                                        ComponentFunctions.GetVisitByPatientAndStage(props.PatientVisits, patient, stage)?.visitType === 1 ? <Call16Regular />
                                                            : <Call16Filled style={{ color: '#b61111' }} />
                                                        : <Call16Regular />)
                                                }{stage.remoteWarning && <Warning16Filled style={{ color: 'orange' }} />}
                                            </div>
                                        </PopoverTrigger>
                                        {ComponentFunctions.IsVisitExist(props.PatientVisits, patient, stage) ?
                                            <PopoverSurface tabIndex={-1}>
                                                {VisitGrid(stage, ComponentFunctions.GetVisitByPatientAndStage(props.PatientVisits, patient, stage), props.ProtocolPatients.find(p => p.numberInProtocol === patient) || null)}
                                            </PopoverSurface> :
                                            <PopoverSurface tabIndex={-1}>
                                                {VisitGrid(stage, null, props.ProtocolPatients.find(p => p.numberInProtocol === patient) || null)}
                                            </PopoverSurface>
                                        }
                                    </Popover>
                                </td>)}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            }
            {visits?.length === 0 &&
                <div className='NoDataContainer'>
                    <img
                        src={require('../../../assets/img/nodata.png')}
                        alt="No Data"
                        style={{ height: '6em' }}
                    ></img>
                    <Text className='NoData'>No hay visitas realizadas</Text>
                </div>
            }
            {Form}
        </>
    )

}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ProtocolActivityComponent as any);